import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InlineMessage,
  ObjectRecordPage,
  type ObjectMetadata,
  PageContent,
  RecordFormDialog,
  Spinner,
} from '@olytic/foundation';
import { type AdminRole, ChangeRoleDialog, MemberTable, parseEmailList, userObjectMetadata } from '@olytic/foundation/admin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { useCurrentUser } from '../auth/AuthProvider';
import { startViewAs } from '../auth/viewAs';
import { useDocumentTitle } from '../documentTitle';
import { SETUP_ROUTES } from '../navigation';
import { SetupPage } from './SetupPages';
import { addUsers, getUser, listUsers, roleCan, type SetupRole, type SetupUser, updateUser, type UserPatch } from './usersApi';

/**
 * Setup → Users: the people in ops.app_user, their role and whether they are
 * active. An active row is what lets someone sign in. Admins only (setupUsers.mjs).
 */

const userPath = (id: string) => `${SETUP_ROUTES.users}/${encodeURIComponent(id)}`;

const NOTHING_SENT =
  "Adding someone here sends them nothing: tell them to sign in with Google using that address. Deactivated people can't sign in; they can still be assigned work and have time logged.";

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');

/* ── /setup/users ──────────────────────────────────────────────── */

type ListLoad = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; users: SetupUser[]; roles: AdminRole[] };

export function SetupUsersPage(): JSX.Element {
  useDocumentTitle('Users');
  const navigate = useNavigate();
  const me = useCurrentUser();
  const [load, setLoad] = useState<ListLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    listUsers(authenticatedFetch).then(
      (list) => live && setLoad({ state: 'ready', ...list }),
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [attempt]);

  if (load.state === 'loading') {
    return (
      <SetupPage title="Users">
        <Spinner label="Loading users…" />
      </SetupPage>
    );
  }
  if (load.state === 'error') {
    return (
      <SetupPage title="Users">
        <ErrorState title="Could not load users" description={load.message} onRetry={() => setAttempt((n) => n + 1)} />
      </SetupPage>
    );
  }

  const { users, roles } = load;
  const currentUserId = users.find((u) => u.email.toLowerCase() === me.email.toLowerCase())?.id;
  const replace = (user: SetupUser) =>
    setLoad({ state: 'ready', roles, users: users.map((u) => (u.id === user.id ? user : u)) });

  return (
    <SetupPage title="Users">
      <InlineMessage tone="info">{NOTHING_SENT}</InlineMessage>
      {notice ? (
        <InlineMessage tone={notice.tone} role="status">
          {notice.text}
        </InlineMessage>
      ) : null}
      <div className="flex justify-end">
        <Button variant="brand" onClick={() => setAdding(true)}>
          Add users
        </Button>
      </div>
      <MemberTable
        members={users}
        roles={roles}
        {...(currentUserId ? { currentUserId } : {})}
        onOpen={(m) => navigate(userPath(m.id))}
        onRoleChange={(m, role) => {
          setNotice(null);
          updateUser(authenticatedFetch, m.id, { role }).then(
            (user) => {
              replace(user);
              setNotice({ tone: 'success', text: `${user.name || user.email} is now ${roles.find((r) => r.key === role)?.label ?? role}.` });
            },
            (error: unknown) => setNotice({ tone: 'error', text: errorText(error) }),
          );
        }}
        rowActions={(m) => [{ key: 'open', label: 'Open', onSelect: () => navigate(userPath(m.id)) }]}
      />
      <RecordFormDialog
        open={adding}
        onOpenChange={setAdding}
        title="Add users"
        submitLabel="Add"
        initialValues={{ role: 'delivery_external' }}
        fields={[
          {
            key: 'emails',
            label: 'Email addresses',
            editor: { kind: 'text' },
            required: true,
            wide: true,
            help: 'One address, or several separated by commas. They sign in with Google as that address; nothing is sent to them.',
          },
          {
            key: 'role',
            label: 'Role',
            editor: { kind: 'select', options: roles.map((r) => ({ value: r.key, label: r.label })) },
            required: true,
            wide: true,
            help: roles.map((r) => `${r.label}: ${r.description ?? ''}`).join(' '),
          },
        ]}
        onSubmit={async (values) => {
          const { valid, invalid } = parseEmailList(values.emails ?? '');
          if (invalid.length > 0) throw new Error(`Not an email address: ${invalid.join(', ')}.`);
          if (valid.length === 0) throw new Error('Enter at least one email address.');
          const result = await addUsers(authenticatedFetch, { emails: valid, role: values.role ?? '' });
          setAdding(false);
          setAttempt((n) => n + 1);
          const parts = [];
          if (result.added.length > 0) parts.push(`Added ${result.added.map((u) => u.email).join(', ')}.`);
          if (result.existing.length > 0) parts.push(`Already users, left as they were: ${result.existing.join(', ')}.`);
          setNotice({ tone: 'success', text: parts.join(' ') });
        }}
      />
    </SetupPage>
  );
}

/* ── /setup/users/:id ──────────────────────────────────────────── */

type RecordLoad =
  | { state: 'loading' }
  | { state: 'missing' }
  | { state: 'error'; message: string }
  | { state: 'ready'; user: SetupUser; roles: SetupRole[] };

function userMetadata(roles: AdminRole[]): ObjectMetadata<SetupUser> {
  const base = userObjectMetadata({ roles });
  const roleLabel = (u: SetupUser) => roles.find((r) => r.key === u.role)?.label ?? u.role;
  return {
    ...base,
    details: [
      { key: 'name', label: 'Name', editable: true, editor: { kind: 'text', nullable: true } },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role', render: roleLabel },
      { key: 'status', label: 'Status', render: (u) => (u.status === 'deactivated' ? 'Deactivated' : 'Active') },
      {
        key: 'access',
        label: 'Can use',
        render: (u) => roles.find((r) => r.key === u.role)?.description ?? '',
      },
    ],
    // No sign-in history is kept yet.
    related: [],
  };
}

export function SetupUserRecordPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const [load, setLoad] = useState<RecordLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [changingRole, setChangingRole] = useState(false);
  const [confirmingStatus, setConfirmingStatus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    getUser(authenticatedFetch, id).then(
      ({ user, roles }) => live && setLoad({ state: 'ready', user, roles }),
      (e: unknown) => {
        if (!live) return;
        if (e instanceof Error && 'status' in e && e.status === 404) setLoad({ state: 'missing' });
        else setLoad({ state: 'error', message: errorText(e) });
      },
    );
    return () => {
      live = false;
    };
  }, [id, attempt]);

  const ready = load.state === 'ready' ? load : null;
  useDocumentTitle(ready ? ready.user.name || ready.user.email : 'User');
  const metadata = useMemo(() => (ready ? userMetadata(ready.roles) : null), [ready]);

  const save = useCallback(
    async (patch: UserPatch) => {
      if (!ready) return;
      setError(null);
      const user = await updateUser(authenticatedFetch, ready.user.id, patch);
      setLoad({ ...ready, user });
    },
    [ready],
  );

  if (load.state === 'loading') {
    return (
      <PageContent as="section" width="wide">
        <Spinner label="Loading user…" />
      </PageContent>
    );
  }
  if (load.state === 'missing') {
    return (
      <PageContent as="section" width="wide">
        <EmptyState
          title="User not found"
          description="The link may be wrong."
          action={
            <Button asChild variant="neutral">
              <Link to={SETUP_ROUTES.users}>All users</Link>
            </Button>
          }
        />
      </PageContent>
    );
  }
  if (load.state === 'error' || !metadata) {
    return (
      <PageContent as="section" width="wide">
        <ErrorState
          title="Could not load this user"
          description={load.state === 'error' ? load.message : ''}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </PageContent>
    );
  }

  const { user, roles } = load;
  const isMe = user.email.toLowerCase() === me.email.toLowerCase();
  const deactivated = user.status === 'deactivated';
  const who = user.name || user.email;
  /** Runs a dialog's save, closing the dialog either way and showing what went wrong. */
  const submit = (patch: UserPatch, close: () => void) => {
    setBusy(true);
    save(patch).then(
      () => {
        setBusy(false);
        close();
      },
      (e: unknown) => {
        setBusy(false);
        close();
        setError(errorText(e));
      },
    );
  };
  return (
    <PageContent as="section" width="wide">
      {error ? (
        <InlineMessage tone="error" role="alert" className="mb-3">
          {error}
        </InlineMessage>
      ) : null}
      <ObjectRecordPage
        metadata={metadata}
        record={user}
        onBack={() => navigate(SETUP_ROUTES.users)}
        actions={
          isMe ? null : (
            <>
              {/* Log in as user: only as an active person whose role may be viewed as; the server decides (impersonation.mjs). */}
              {deactivated || !roleCan(roles, user.role, 'be_impersonated') ? null : (
                <Button
                  variant="neutral"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    setError(null);
                    startViewAs(user.id).catch((e: unknown) => {
                      setBusy(false);
                      setError(errorText(e));
                    });
                  }}
                >
                  Log in as user
                </Button>
              )}
              <Button variant="neutral" onClick={() => setChangingRole(true)}>
                Change role
              </Button>
              <Button variant={deactivated ? 'neutral' : 'outline'} onClick={() => setConfirmingStatus(true)}>
                {deactivated ? 'Reactivate' : 'Deactivate'}
              </Button>
            </>
          )
        }
        onFieldsSave={(changes) => ('name' in changes ? save({ name: changes.name ?? null }) : undefined)}
      />
      <ChangeRoleDialog
        open={changingRole}
        onOpenChange={setChangingRole}
        memberName={user.name || user.email}
        roles={roles}
        value={user.role}
        busy={busy}
        onSubmit={(role) => submit({ role }, () => setChangingRole(false))}
      />
      <ConfirmDialog
        open={confirmingStatus}
        onOpenChange={setConfirmingStatus}
        title={deactivated ? `Reactivate ${who}?` : `Deactivate ${who}?`}
        description={
          deactivated
            ? `${who} can sign in to Mothership again, with the role they had.`
            : `${who} can no longer sign in or see projects. They stay a resource: work can still be assigned to them and time logged for them.`
        }
        confirmLabel={deactivated ? 'Reactivate' : 'Deactivate'}
        tone={deactivated ? 'default' : 'danger'}
        busy={busy}
        onConfirm={() => submit({ active: deactivated }, () => setConfirmingStatus(false))}
      />
    </PageContent>
  );
}
